import { useState } from 'react';
import { X, CalendarClock, AlertTriangle, Loader2, Mail } from 'lucide-react';
import { Spinner } from 'react-bootstrap';
import type { FirstChargePostponePreview } from '@/features/contracts/api/contractApi';
import { formatPrice } from '@/features/contracts/utils/contractFormatter';

interface PostponeFirstChargeModalProps {
  isOpen: boolean;
  preview: FirstChargePostponePreview | null;
  isLoadingPreview: boolean;
  previewError: string | null;
  isConfirming: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

const formatFrenchDate = (iso: string): string => {
  try {
    return new Date(iso).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      timeZone: 'Europe/Paris',
    });
  } catch {
    return iso;
  }
};

/**
 * Report du 1er prélèvement d'un contrat mensualisé à sa date de souscription
 * (admin INTERNAL) : récapitulatif renvoyé par la simulation, puis confirmation.
 */
export const PostponeFirstChargeModal: React.FC<PostponeFirstChargeModalProps> = ({
  isOpen,
  preview,
  isLoadingPreview,
  previewError,
  isConfirming,
  onClose,
  onConfirm,
}) => {
  // Aperçu de l'e-mail ouvert pour CE récapitulatif : un nouveau récapitulatif
  // (autre ouverture, autre contrat) le referme de lui-même.
  const [emailPreviewFor, setEmailPreviewFor] = useState<FirstChargePostponePreview | null>(null);

  if (!isOpen) return null;

  const canConfirm = !!preview && !isLoadingPreview && !isConfirming && !previewError;
  const payment = preview?.payment ?? null;
  const emailPreview = preview?.emailPreview ?? null;
  const isEmailPreviewOpen = !!emailPreview && emailPreviewFor === preview;

  return (
    <div
      className="tw-fixed tw-inset-0 tw-bg-black tw-bg-opacity-50 tw-flex tw-items-center tw-justify-center tw-z-[100] tw-p-4"
      onClick={isConfirming ? undefined : onClose}
    >
      <div
        className="tw-bg-white tw-rounded-lg tw-shadow-xl tw-max-w-2xl tw-w-full tw-max-h-[90vh] tw-overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="tw-flex tw-items-center tw-justify-between tw-p-4 md:tw-p-6 tw-border-b tw-border-gray-200 tw-sticky tw-top-0 tw-bg-white tw-z-10">
          <div className="tw-flex tw-items-center tw-gap-3">
            <h2 className="tw-text-xl tw-font-bold tw-text-gray-900">
              Rembourser et prélever à la date de début
            </h2>
            <span className="tw-px-2 tw-py-0.5 tw-text-xs tw-font-semibold tw-bg-amber-100 tw-text-amber-700 tw-rounded">
              Admin
            </span>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={isConfirming}
            className="tw-text-gray-400 hover:tw-text-gray-600 tw-transition-colors disabled:tw-opacity-50"
            aria-label="Fermer"
          >
            <X className="tw-w-5 tw-h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="tw-p-4 md:tw-p-6 tw-space-y-4">
          {isLoadingPreview && (
            <div className="tw-flex tw-items-center tw-justify-center tw-py-12 tw-gap-3">
              <Spinner animation="border" size="sm" />
              <span className="tw-text-sm tw-text-gray-500">
                Chargement du récapitulatif…
              </span>
            </div>
          )}

          {previewError && !isLoadingPreview && (
            <div className="tw-bg-red-50 tw-border tw-border-red-200 tw-rounded-lg tw-p-4 tw-flex tw-items-start tw-gap-3">
              <AlertTriangle className="tw-w-5 tw-h-5 tw-text-red-600 tw-flex-shrink-0 tw-mt-0.5" />
              <div className="tw-text-sm tw-text-red-700">
                <p className="tw-font-semibold tw-mb-1">Action impossible</p>
                <p className="tw-mb-0">{previewError}</p>
              </div>
            </div>
          )}

          {preview && !isLoadingPreview && !previewError && (
            <>
              <div className="tw-bg-blue-50 tw-border tw-border-blue-200 tw-rounded-lg tw-p-3 tw-text-sm tw-text-blue-800">
                Les mensualités commenceront à la <strong>date de début du contrat</strong>, sur
                le même abonnement et le même mandat SEPA.
                {payment && (
                  <>
                    {' '}Le prélèvement déjà fait est <strong>remboursé au client</strong> et la
                    commission Affinicar est <strong>rendue au vendeur</strong>.
                  </>
                )}
              </div>

              <div className="tw-border tw-border-gray-200 tw-rounded-lg tw-overflow-hidden">
                <table className="tw-w-full tw-text-sm">
                  <tbody>
                    <tr className="tw-border-b tw-border-gray-100">
                      <td className="tw-px-4 tw-py-2.5 tw-text-gray-600 tw-w-1/2">Contrat</td>
                      <td className="tw-px-4 tw-py-2.5 tw-font-medium tw-text-gray-900">
                        {preview.contractLabel}
                      </td>
                    </tr>
                    {payment && (
                      <>
                        <tr className="tw-border-b tw-border-gray-100 tw-bg-amber-50">
                          <td className="tw-px-4 tw-py-2.5 tw-text-gray-700">
                            Prélèvement remboursé
                          </td>
                          <td className="tw-px-4 tw-py-2.5 tw-font-bold tw-text-amber-900">
                            {formatPrice(payment.amountCents / 100)}
                            <span className="tw-ml-2 tw-text-xs tw-font-normal tw-text-amber-800">
                              du {formatFrenchDate(payment.paymentDate)}
                              {payment.status === 'processing' && ', remboursé dès son encaissement'}
                            </span>
                          </td>
                        </tr>
                        <tr className="tw-border-b tw-border-gray-100">
                          <td className="tw-px-4 tw-py-2.5 tw-text-gray-600">
                            Commission rendue au vendeur
                          </td>
                          <td className="tw-px-4 tw-py-2.5 tw-text-gray-900">
                            {formatPrice(payment.commissionCents / 100)}
                          </td>
                        </tr>
                      </>
                    )}
                    <tr className="tw-border-b tw-border-gray-100">
                      <td className="tw-px-4 tw-py-2.5 tw-text-gray-600">1er prélèvement</td>
                      <td className="tw-px-4 tw-py-2.5 tw-font-medium tw-text-gray-900">
                        {formatFrenchDate(preview.firstChargeAt)}
                      </td>
                    </tr>
                    <tr className="tw-border-b tw-border-gray-100">
                      <td className="tw-px-4 tw-py-2.5 tw-text-gray-600">Mensualités</td>
                      <td className="tw-px-4 tw-py-2.5 tw-text-gray-900">
                        {preview.iterations} × {formatPrice(preview.monthlyAmountCents / 100)}
                      </td>
                    </tr>
                    <tr>
                      <td className="tw-px-4 tw-py-2.5 tw-text-gray-600">Dernière mensualité</td>
                      <td className="tw-px-4 tw-py-2.5 tw-text-gray-900">
                        {formatFrenchDate(preview.lastChargeAt)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {preview.emails && (
                <div className="tw-border tw-border-gray-200 tw-rounded-lg tw-p-3 tw-text-sm tw-text-gray-700">
                  <p className="tw-flex tw-items-center tw-gap-2 tw-font-semibold tw-text-gray-900 tw-mb-2">
                    <Mail className="tw-w-4 tw-h-4 tw-text-gray-500 tw-flex-shrink-0" />
                    E-mails envoyés
                  </p>
                  {preview.emails.beneficiary ? (
                    <>
                      <ul className="tw-list-disc tw-pl-5 tw-mb-2 tw-space-y-1">
                        <li>
                          Dès la confirmation : « Vos mensualités commencent à la date de début de
                          votre contrat », avec les dates et le montant des mensualités
                          {payment && ' et le remboursement'}.
                          {emailPreview && (
                            <>
                              {' '}
                              <button
                                type="button"
                                onClick={() => setEmailPreviewFor(preview)}
                                className="tw-text-blue-700 hover:tw-text-blue-900 tw-underline tw-font-medium"
                              >
                                Voir l&apos;e-mail
                              </button>
                            </>
                          )}
                        </li>
                        <li>
                          Le {formatFrenchDate(preview.firstChargeAt)}, au 1er prélèvement : la
                          confirmation du paiement, avec la facture.
                        </li>
                      </ul>
                      <p className="tw-mb-0 tw-break-words">
                        À : <strong>{preview.emails.beneficiary}</strong>
                        {preview.emails.vendorCopy && (
                          <>
                            {' '}· Copie au vendeur : <strong>{preview.emails.vendorCopy}</strong>
                          </>
                        )}
                      </p>
                    </>
                  ) : (
                    <p className="tw-mb-0">
                      Le client n&apos;a pas d&apos;adresse e-mail : aucun e-mail ne lui sera envoyé.
                    </p>
                  )}
                </div>
              )}

              {preview.subscriptionAlreadyPostponed && (
                <div className="tw-bg-gray-50 tw-border tw-border-gray-200 tw-rounded-lg tw-p-3 tw-text-sm tw-text-gray-700">
                  Les prélèvements sont déjà décalés à la date de début (action précédente
                  interrompue) : seul le remboursement sera demandé.
                </div>
              )}

              <div className="tw-bg-amber-50 tw-border tw-border-amber-200 tw-rounded-lg tw-p-3 tw-text-sm tw-text-amber-900 tw-flex tw-items-start tw-gap-2">
                <AlertTriangle className="tw-w-4 tw-h-4 tw-text-amber-600 tw-flex-shrink-0 tw-mt-0.5" />
                <span>
                  Le remboursement est prélevé sur le solde Stripe du vendeur : s&apos;il ne suffit
                  pas, ses virements sont suspendus jusqu&apos;au remboursement.
                </span>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="tw-flex tw-justify-end tw-gap-3 tw-p-4 md:tw-p-6 tw-border-t tw-border-gray-200 tw-bg-gray-50">
          <button
            type="button"
            onClick={onClose}
            disabled={isConfirming}
            className="tw-px-4 tw-py-2 tw-border tw-border-gray-300 tw-rounded-md tw-text-sm tw-font-medium tw-text-gray-700 tw-bg-white hover:tw-bg-gray-50 disabled:tw-opacity-50 tw-transition-colors"
          >
            Annuler
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={!canConfirm}
            className="tw-flex tw-items-center tw-gap-2 tw-px-4 tw-py-2 tw-rounded-md tw-text-sm tw-font-medium tw-text-white tw-bg-amber-600 hover:tw-bg-amber-700 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed tw-transition-colors"
          >
            {isConfirming ? (
              <>
                <Loader2 className="tw-w-4 tw-h-4 tw-animate-spin" />
                Mise à jour en cours…
              </>
            ) : (
              <>
                <CalendarClock className="tw-w-4 tw-h-4" />
                {payment ? 'Rembourser et confirmer' : 'Confirmer'}
              </>
            )}
          </button>
        </div>
      </div>

      {/* Aperçu de l'e-mail au client, tel qu'il sera envoyé (HTML généré par l'API) */}
      {isEmailPreviewOpen && emailPreview && (
        <div
          className="tw-fixed tw-inset-0 tw-bg-black tw-bg-opacity-50 tw-flex tw-items-center tw-justify-center tw-z-[110] tw-p-4"
          onClick={(e) => {
            e.stopPropagation();
            setEmailPreviewFor(null);
          }}
        >
          <div
            className="tw-bg-white tw-rounded-lg tw-shadow-xl tw-max-w-3xl tw-w-full tw-h-[85vh] tw-flex tw-flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="tw-flex tw-items-center tw-justify-between tw-p-4 tw-border-b tw-border-gray-200">
              <h3 className="tw-text-lg tw-font-bold tw-text-gray-900 tw-m-0">
                Aperçu de l&apos;e-mail au client
              </h3>
              <button
                type="button"
                onClick={() => setEmailPreviewFor(null)}
                className="tw-text-gray-400 hover:tw-text-gray-600 tw-transition-colors"
                aria-label="Fermer l'aperçu"
              >
                <X className="tw-w-5 tw-h-5" />
              </button>
            </div>
            <div className="tw-flex-1 tw-flex tw-flex-col tw-gap-2 tw-p-4 tw-min-h-0">
              <p className="tw-text-sm tw-text-gray-600 tw-m-0 tw-break-words">
                Objet : <strong className="tw-text-gray-900">{emailPreview.subject}</strong>
              </p>
              {preview?.emails?.beneficiary && (
                <p className="tw-text-sm tw-text-gray-600 tw-m-0 tw-break-words">
                  À : <strong className="tw-text-gray-900">{preview.emails.beneficiary}</strong>
                  {preview.emails.vendorCopy && (
                    <>
                      {' '}· Copie : <strong className="tw-text-gray-900">{preview.emails.vendorCopy}</strong>
                    </>
                  )}
                </p>
              )}
              <iframe
                title="Aperçu de l'e-mail au client"
                srcDoc={emailPreview.htmlContent}
                sandbox=""
                className="tw-w-full tw-flex-1 tw-min-h-[360px] tw-border tw-border-gray-200 tw-rounded-md tw-bg-gray-50"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
